import type { ExteriorMaterialGeometry } from "building-types/geometry/exteriorMaterialGeometry";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { InteriorMaterial } from "building-types/materials/interiorPaletteTypes";
import type { ProfileId } from "building-types/profiles/profileTypes";
import uid from "common/utils/uid";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { UVTransformation } from "geom/uv";
import type { PrimaryEntityId } from "../doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "../entities/docEntityBase";
import { docEntityBaseNew } from "../entities/docEntityBaseNew";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { ResolvedWallPlaneSide, WallPlaneOperationBoundary } from "./wallPlane";
import type { WallPlaneMaterialFinish } from "./wallPlaneAssemblyTypes";

export interface WallPlaneTrimEntityBase {
  readonly ruleId: ForeignEntityId<"WALL_PLANE_RULE">;
  readonly path: PolyLine2;
  readonly profileId: ProfileId;
  readonly materialTransformation?: Partial<UVTransformation>;
}

export interface WallPlaneTrimEntity extends FormulaDisabledDocEntityBase<"WALL_PLANE_TRIM">, WallPlaneTrimEntityBase {
  readonly id: PrimaryEntityId<"WALL_PLANE_TRIM">;
  readonly finish?: WallPlaneMaterialFinish;
}

export type WallPlaneTrimFinishType = WallPlaneMaterialFinish["finishType"];

export function wallPlaneTrimFinishType(
  trim: Pick<WallPlaneTrimEntity, "finish">,
): WallPlaneTrimFinishType | undefined {
  return trim.finish?.finishType;
}

export function wallPlaneTrimMaterial(trim: WallPlaneTrimEntity): ExteriorMaterial | InteriorMaterial | undefined {
  return trim.finish?.finishPalette;
}

export interface ResolvedWallPlaneTrimContext {
  readonly ruleId: string;
  readonly planeHash: string;
  readonly resolvedFaceSide: ResolvedWallPlaneSide;
  readonly operationBoundary?: WallPlaneOperationBoundary;
}

export interface WallPlaneTrimRail {
  readonly rail: PolyLine3;
  readonly profileId: ProfileId;
  readonly profileName: string;
  readonly material: ExteriorMaterial;
}

export interface WallPlaneTrimGeometry {
  readonly sourceId: string;
  readonly ruleId: string;
  readonly planeHash: string;
  readonly resolvedFaceSide: ResolvedWallPlaneSide;
  readonly geometry: ExteriorMaterialGeometry;
  readonly rails: WallPlaneTrimRail[];
}

export function wallPlaneTrimEntityNew({
  name = "Wall Plane Trim",
  id = uid(),
  sortIndex = 0,
  ruleId,
  path,
  profileId,
  finish,
  materialTransformation,
  disabled,
}: Partial<
  Pick<
    WallPlaneTrimEntity,
    "name" | "id" | "sortIndex" | "ruleId" | "path" | "profileId" | "finish" | "materialTransformation" | "disabled"
  >
> & {
  readonly ruleId: ForeignEntityId<"WALL_PLANE_RULE">;
  readonly path: PolyLine2;
  readonly profileId: ProfileId;
}): WallPlaneTrimEntity {
  return {
    ...docEntityBaseNew("WALL_PLANE_TRIM", id),
    name,
    sortIndex,
    ruleId,
    path,
    profileId,
    finish,
    materialTransformation,
    disabled,
  };
}
